import { useEffect, useMemo, useState } from "react";

import { sortBy } from 'lodash'

import createContextFactory from "@Utils/factory";

import { useGridColumnsConfig } from './hooks/columnsConfig'
import { useStateGrid } from "./hooks/state";

function useGridFactory({ store }) {
  const [definitions, setDefinitions] = useState(null)
  const [columns, setColumns] = useState(null)
  const [filters, setFilters] = useState(null)
  const [events, setEvents] = useState(null)
  const [rowsSelected, setRowsSelected] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [columnsConfigOpen, setColumnsConfigOpen] = useState(false)

  const state = useStateGrid(definitions || {})
  const { options, term, autoRefresh } = state
  const { collections: { useLoad, list: handleRefresh } } = store
  const { defaultParams } = definitions || {}
  useLoad(useMemo(() => ({ options, term, autoRefresh, ...defaultParams }), [options, term, autoRefresh, defaultParams]))

  const gridKey = useMemo(() => definitions && gridColumnsConfigKey(definitions), [definitions])
  const {
    hiddenColumns: hiddenColumnsState, setHiddenColumns,
    columnOrder, setColumnOrder,
    pinnedColumns: pinnedColumnsState, setPinnedColumns,
    resetColumnsConfig,
  } = useGridColumnsConfig(gridKey)

  const defaultPinnedColumns = useMemo(() => (columns || []).filter(column => column.pinned).map(columnKey), [columns])
  const pinnedColumns = pinnedColumnsState ?? defaultPinnedColumns

  const defaultHiddenColumns = useMemo(() => (columns || []).filter(isColumnHiddenByDefault).map(columnKey), [columns])
  const hiddenColumns = hiddenColumnsState ?? defaultHiddenColumns

  const visibleColumns = useMemo(() => {
    if (!columns) {
      return columns
    }

    const ordered = sortBy(columns, [
      column => isColumnConfigurable(column),
      column => !pinnedColumns.includes(columnKey(column)),
      column => {
        const index = columnOrder.indexOf(columnKey(column))
        return index === -1 ? Infinity : index
      },
    ])

    return ordered.filter(column => !isColumnConfigurable(column) || !hiddenColumns.includes(columnKey(column)))
  }, [columns, columnOrder, pinnedColumns, hiddenColumns])

  return useMemo(() => ({
    state, store,
    definitions, setDefinitions,
    columns, setColumns,
    hiddenColumns, setHiddenColumns,
    columnOrder, setColumnOrder,
    pinnedColumns, setPinnedColumns,
    defaultPinnedColumns, defaultHiddenColumns, resetColumnsConfig,
    visibleColumns,
    filters, setFilters,
    events, setEvents,
    rowsSelected, setRowsSelected,
    showFilters, setShowFilters,
    columnsConfigOpen, setColumnsConfigOpen,
    handleRefresh,
  }), [
    columns, hiddenColumns, setHiddenColumns, columnOrder, setColumnOrder, pinnedColumns, setPinnedColumns,
    defaultPinnedColumns, defaultHiddenColumns, resetColumnsConfig, visibleColumns, definitions, events, filters, state, store,
    rowsSelected, showFilters, columnsConfigOpen, handleRefresh,
  ])
}

export function gridColumnsConfigKey({ entityNameModule = '', entityNamePlural = '' }) {
  return `${entityNameModule}:${entityNamePlural}`
}

export function columnKey({ id, accessorKey }) {
  return id ?? accessorKey
}

export function isColumnConfigurable({ enableColumnConfig = true }) {
  return enableColumnConfig
}

export function isColumnHiddenByDefault({ defaultVisible = true }) {
  return !defaultVisible
}

export { resolveColumnOrder } from '@Components/ColumnsConfig/utils'

export function useBuildGrid({ definitions, columns, filters, events }) {
  const { setDefinitions, setColumns, setFilters, setEvents } = useGrid()

  useEffect(() => {
    setDefinitions(definitions)
  }, [definitions, setDefinitions])

  useEffect(() => {
    setColumns(columns)
  }, [columns, setColumns])

  useEffect(() => {
    setFilters(filters)
  }, [filters, setFilters])

  useEffect(() => {
    setEvents(events)
  }, [events, setEvents])
}

export function useGridBulkActions() {
  const { definitions } = useGrid()
  return definitions?.bulkActions ?? null
}

const {
  Provider: GridProvider,
  useContext: useGrid,
} = createContextFactory(useGridFactory)

export {
  useGrid,
  GridProvider,
}
